import { serializeError } from 'serialize-error';
import { randomString } from '../../helpers/randomString';

let skipGettingStorage = false;
let fallbackClientUid: string | undefined = undefined;
const CLIENT_UID_STORAGE_KEY = 'clientUniqueId';
const CLIENT_UID_STR_LENGTH = 12;

const getLocalStorage = (): Storage | null => {
  try {
    return skipGettingStorage ? null : window.localStorage;
  } catch (err: unknown) {
    console.error('Get local storage error', { error: serializeError(err) });

    if (err instanceof Error && err.name === 'SecurityError') {
      skipGettingStorage = true; // to reduce extra logs
    }

    return null;
  }
};

export function setLocalStorageItem(itemName: string, itemValue: unknown): void {
  const localStorage = getLocalStorage();

  if (!localStorage) {
    return;
  }

  try {
    localStorage.setItem(itemName, JSON.stringify(itemValue));
  } catch (err) {
    console.error(err);
  }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function getLocalStorageItem<T>(itemName: string): T | null {
  const localStorage = getLocalStorage();

  if (!localStorage) {
    return null;
  }

  try {
    const item = localStorage.getItem(itemName);

    if (!item) {
      return null;
    }

    return JSON.parse(item) as T;
  } catch {
    return null;
  }
}

export const generateAndSetClientUniqueId = (): string => {
  if (fallbackClientUid) {
    return fallbackClientUid;
  }

  const existingClientUniqueId = getLocalStorageItem<string>(CLIENT_UID_STORAGE_KEY);

  if (existingClientUniqueId) {
    fallbackClientUid = existingClientUniqueId;

    return existingClientUniqueId;
  }

  const clientUniqueId = randomString(CLIENT_UID_STR_LENGTH);
  fallbackClientUid = clientUniqueId;
  setLocalStorageItem(CLIENT_UID_STORAGE_KEY, clientUniqueId);

  return clientUniqueId;
};

export const getClientUniqueId = (): string => {
  const id = getLocalStorageItem<string>(CLIENT_UID_STORAGE_KEY);

  if (id) {
    return id;
  }

  return fallbackClientUid ?? generateAndSetClientUniqueId();
};

export const receiveClientTrackingId = (): string => getClientUniqueId();
